import axios from './axiosInstance';
import type {
  APIPackCatalog,
  APIPackCatalogEntry,
  APIPackChecklistBody,
  APIPackInstallation,
  APIPackInstallationCreateBody,
  APIPackInstallationMergeBody,
  APIPackRun,
  APIPackRunCreateBody,
  APIPackRunUpdateBody,
} from '../../types';

// Onboarding packs (API v21; every pack route answers `400 invalid api
// version` below that). Contract: UllrServerless
// docs/specs/onboarding-packs/API-CONTRACT.md.
//
// Errors are thrown as they come — callers read `error.response.status`:
// the install record is AREA-wide, so a 404 on an item route is "not
// installed", and a 404 on the catalog is a stage that predates the routes.

interface SportAreaParams {
  sport: string;
  areaId: string;
}

interface PackParams extends SportAreaParams {
  packKey: string;
}

interface RunParams extends PackParams {
  runId: string;
}

/**
 * Idempotent routes honour `x-request-id`, keyed on the header AND the caller.
 * Only a 2xx is saved, so keep ONE id per logical operation across retries —
 * never mint a fresh id after a failure (contract §1).
 */
interface IdempotentParams {
  requestId?: string;
}

const requestIdHeaders = (requestId?: string) => ({
  headers: {
    ...(requestId ? { 'x-request-id': requestId } : {}),
  },
});

const packsUrl = ({ sport, areaId }: SportAreaParams) => `/${sport}/areas/${areaId}/packs`;
const packUrl = ({ sport, areaId, packKey }: PackParams) => (
  `${packsUrl({ sport, areaId })}/${encodeURIComponent(packKey)}`
);
const runsUrl = (params: PackParams) => `${packUrl(params)}/runs`;
const runUrl = ({ runId, ...params }: RunParams) => `${runsUrl(params)}/${encodeURIComponent(runId)}`;

/** Route 1. Manifests of the bundled packs. Needs `packs:read` at ANY area. */
const getCatalog = (): Promise<APIPackCatalog> => {
  return axios.get('/packs/catalog')
    .then((response) => response.data);
};

/** Route 2. One pack's full contents, lock file included. 404: unknown (or rejected) pack. */
const getCatalogPack = ({ packKey }: { packKey: string }): Promise<APIPackCatalogEntry> => {
  return axios.get(`/packs/catalog/${encodeURIComponent(packKey)}`)
    .then((response) => response.data);
};

/** Route 3. The resort's install records, sorted by `packKey`; `[]` when nothing is installed. */
const getInstallations = ({ sport, areaId }: SportAreaParams): Promise<APIPackInstallation[]> => {
  return axios.get(packsUrl({ sport, areaId }))
    .then((response) => response.data);
};

/** Route 4. 404 when the pack is not installed at this resort. */
const getInstallation = ({ sport, areaId, packKey }: PackParams): Promise<APIPackInstallation> => {
  return axios.get(packUrl({ sport, areaId, packKey }))
    .then((response) => response.data);
};

/**
 * Route 5 (idempotent). Create the install record. `sport` must be one of the
 * pack's `manifest.sports` (else 400). 409: already installed.
 */
const createInstallation = ({
  sport,
  areaId,
  requestId,
  ...body
}: SportAreaParams & IdempotentParams & APIPackInstallationCreateBody): Promise<APIPackInstallation> => {
  return axios.put(packsUrl({ sport, areaId }), body, requestIdHeaders(requestId))
    .then((response) => response.data);
};

/**
 * Route 6 (idempotent). Merge `entities` (each record to its own path) and
 * replace `status` / `mapping` / `drift`. 409: the record is locked by a
 * concurrent write — retry with the SAME `requestId`.
 */
const mergeInstallation = ({
  sport,
  areaId,
  packKey,
  requestId,
  ...body
}: PackParams & IdempotentParams & APIPackInstallationMergeBody): Promise<APIPackInstallation> => {
  return axios.post(packUrl({ sport, areaId, packKey }), body, requestIdHeaders(requestId))
    .then((response) => response.data);
};

/**
 * Route 12 (idempotent). Remove an EMPTY install record and its runs; 409 while
 * entries are still recorded (take them down first — the uninstall engine).
 */
const uninstallInstallation = ({
  sport,
  areaId,
  packKey,
  requestId,
}: PackParams & IdempotentParams): Promise<{ packKey: string; runsDeleted: number }> => {
  return axios.delete(packUrl({ sport, areaId, packKey }), requestIdHeaders(requestId))
    .then((response) => response.data);
};

/**
 * Route 7. Toggle MANUAL checklist items and/or set the assignee (`null`
 * clears it). 400 for an auto item. Returns the record with `level` re-evaluated.
 */
const updateChecklist = ({
  sport,
  areaId,
  packKey,
  ...body
}: PackParams & APIPackChecklistBody): Promise<APIPackInstallation> => {
  return axios.post(`${packUrl({ sport, areaId, packKey })}/checklist`, body)
    .then((response) => response.data);
};

interface GetRunsParams extends PackParams {
  /** Default 50, max 200; with `expandCreated` default AND max are 10. */
  limit?: number;
  /** Carry `created` on each run. */
  expandCreated?: boolean;
}

/** Route 8. Newest first. `[]` for a pack with no runs, installed or not. */
const getRuns = ({ sport, areaId, packKey, limit, expandCreated }: GetRunsParams): Promise<APIPackRun[]> => {
  return axios.get(runsUrl({ sport, areaId, packKey }), {
    params: {
      ...(limit !== undefined ? { limit } : {}),
      ...(expandCreated ? { expand: 'created' } : {}),
    },
  })
    .then((response) => response.data);
};

/** Route 9. One run, ALWAYS with `created`. */
const getRun = ({ sport, areaId, packKey, runId }: RunParams): Promise<APIPackRun> => {
  return axios.get(runUrl({ sport, areaId, packKey, runId }))
    .then((response) => response.data);
};

/**
 * Route 10 (idempotent). Create a run (`status: "running"`). Always mint
 * `runId` client-side: a replay after an unanswered request is then a 409
 * rather than an orphan second run.
 */
const createRun = ({
  sport,
  areaId,
  packKey,
  requestId,
  ...body
}: PackParams & IdempotentParams & APIPackRunCreateBody): Promise<APIPackRun> => {
  return axios.put(runsUrl({ sport, areaId, packKey }), body, requestIdHeaders(requestId))
    .then((response) => response.data);
};

/**
 * Route 11 (idempotent). Append `created`, replace `counts`, set `status` /
 * `error`. A finished run is final (409). After an UNANSWERED append, read the
 * run and compare `createdCount` before re-sending — a replay appends twice.
 */
const updateRun = ({
  sport,
  areaId,
  packKey,
  runId,
  requestId,
  ...body
}: RunParams & IdempotentParams & APIPackRunUpdateBody): Promise<APIPackRun> => {
  return axios.post(runUrl({ sport, areaId, packKey, runId }), body, requestIdHeaders(requestId))
    .then((response) => response.data);
};

const packFunctions = {
  getCatalog,
  getCatalogPack,
  getInstallations,
  getInstallation,
  createInstallation,
  mergeInstallation,
  uninstallInstallation,
  updateChecklist,
  getRuns,
  getRun,
  createRun,
  updateRun,
};

export default packFunctions;
